import { createWithEqualityFn } from 'zustand/traditional';

import { MajorDetail } from '@common-types';

import { baseService } from '@services/base.service';
import useUserInfoStore from '@store/UserInfoStore';

import { USHE_GROUPED_SCHOOLS_LIMIT } from '@constants/stateSpotlight';

export interface MajorDetailsStore {
  currentCipCode: string;
  currentUsheFirst: boolean;
  majorDetailData: MajorDetail;
  isMajorDetailDataLoading: boolean;
  getMajorDetailData: (cipCode: string, usheFirst?: boolean) => Promise<void>;
}

const initialState = {
  currentCipCode: '',
  currentUsheFirst: false,
  majorDetailData: {},
  isMajorDetailDataLoading: false,
} as MajorDetailsStore;

const NEARBY_SCHOOLS_FILTERS =
  'classesOffered=4' + '&carnegieSizeSettings=2-year,4-year' + '&institutionTypes=Public,Private (non-profit)&limit=8';

const USHE_GROUPED_SCHOOLS_FILTERS = `usheFirst=true&limit=${USHE_GROUPED_SCHOOLS_LIMIT}`;

let latestRequestId = 0;

export const useMajorDetailsStore = createWithEqualityFn<MajorDetailsStore>()(
  (set, get) => ({
    ...initialState,

    getMajorDetailData: async (cipCode: string, usheFirst = false) => {
      latestRequestId += 1;

      const requestId = latestRequestId;
      const { currentCipCode, currentUsheFirst } = get();

      if (currentCipCode === cipCode && currentUsheFirst === usheFirst) {
        if (get().isMajorDetailDataLoading) set({ isMajorDetailDataLoading: false });

        return;
      }

      set({ isMajorDetailDataLoading: true });

      const zipCode = useUserInfoStore.getState().getPostalCode();

      const userLocationData = zipCode ? `&zipCode=${zipCode}` : '';

      const filtersUrl = usheFirst ? USHE_GROUPED_SCHOOLS_FILTERS : NEARBY_SCHOOLS_FILTERS;

      try {
        const majorDetailData = await baseService.getAsync<MajorDetail>(
          `/major-detail/${cipCode}?${filtersUrl}${userLocationData}`,
        );

        if (latestRequestId !== requestId) return;

        set({
          majorDetailData,
          currentCipCode: cipCode,
          currentUsheFirst: usheFirst,
          isMajorDetailDataLoading: false,
        });
      } finally {
        if (latestRequestId === requestId && get().isMajorDetailDataLoading) {
          set({ isMajorDetailDataLoading: false });
        }
      }
    },
  }),
  Object.is,
);
